// Coincidencia de artículo para el despliegue automático de la subtabla de un
// pedido de venta, y utilidades comunes de los filtros de artículo.
//
// Al buscar un artículo en la lista, salen los pedidos que lo contienen; al
// desplegar uno de ellos, sus facturas y cargas con ese artículo arrancan
// abiertas y el resto colapsado. Este módulo decide qué cuenta como coincidencia.
//
// ── EL CONTRATO DE LOS FILTROS DE ARTÍCULO ──────────────────────────────────
// Se MUESTRA el código formateado (`useFmtArticulo`, que para el rol avp recorta
// la letra de tipo: `QARF…` → `ARF…`) y se FILTRA por el código crudo. El valor
// que viaja al predicado y a la URL no cambia nunca: sigue siendo el de la
// vista, porque es el único que casa con el dato. Lo que cambia es lo que se
// pinta —opciones del desplegable, chips de la franja de filtros activos— y lo
// que se puede teclear, que tiene que ser lo que el usuario ve en la tabla.
import type { OpcionMulti } from '@/components/common/MultiFiltro'

/** Formateador de código de artículo, tal y como lo devuelve `useFmtArticulo`. */
export type FmtArticulo = (code: string | null | undefined) => string

/** Decide si un artículo casa con lo que se está buscando. */
export type CoincideArticulo = (articulo?: string, descripcion?: string) => boolean

/** Predicado de coincidencia de un artículo con el texto del buscador, o `null`
 *  si no hay nada que buscar — con `null` la pantalla se comporta como siempre:
 *  todo colapsado.
 *
 *  Se compara contra código **y** descripción, el mismo criterio que el filtro de
 *  la lista (`articulos_busqueda` = artículo + descripción). Si sólo mirara el
 *  código, un pedido podría aparecer en la búsqueda por su descripción y luego no
 *  desplegar nada al abrirlo.
 *
 *  `fmt` es el formateador de código (`useFmtArticulo`): el código se compara
 *  también formateado, porque es el que se ve en pantalla y el que el usuario
 *  copia y pega en el buscador. */
export function predicadoArticulo(
  filtro: string | undefined,
  fmt: FmtArticulo,
): CoincideArticulo | null {
  const q = (filtro ?? '').trim().toLowerCase()
  if (!q) return null
  return (articulo, descripcion) =>
    (articulo ?? '').toLowerCase().includes(q) ||
    fmt(articulo).toLowerCase().includes(q) ||
    (descripcion ?? '').toLowerCase().includes(q)
}

/** Igual que `predicadoArticulo` pero con varios términos, combinados con OR.
 *
 *  Es lo que necesita el buscador multi-término de las listas: «enséñame lo de
 *  esta referencia O de esta otra». Con un solo término se comporta exactamente
 *  igual que el de arriba, así que las pantallas que aún no acumulan términos no
 *  cambian de comportamiento. */
export function predicadoArticulos(
  terminos: string[] | undefined,
  fmt: FmtArticulo,
): CoincideArticulo | null {
  const predicados = (terminos ?? [])
    .map(term => predicadoArticulo(term, fmt))
    .filter((p): p is CoincideArticulo => !!p)
  if (predicados.length === 0) return null
  return (articulo, descripcion) => predicados.some(p => p(articulo, descripcion))
}

/** Los dos textos por los que se puede teclear un código de artículo: el CRUDO
 *  —el que trae la vista, el que llega si se elige del catálogo o se pega desde
 *  D365— y el FORMATEADO, que es el que se ve en la tabla y el que el usuario
 *  copia. Devuelve sólo el crudo cuando no hay diferencia, para no duplicar
 *  texto en el heno de búsqueda.
 *
 *  Buscar únicamente por el formateado era el fallo: elegir `QARF32101314` en el
 *  desplegable no encontraba nada, porque el heno sólo decía `ARF32101314`. */
export function codigosBuscables(
  articulo: string | null | undefined,
  fmt: FmtArticulo,
): string {
  const crudo = (articulo ?? '').trim()
  const vista = fmt(crudo)
  return vista && vista !== crudo ? `${crudo} ${vista}` : crudo
}

/** Heno de búsqueda de una fila con código de artículo, ya en minúsculas.
 *
 *  Concentra en un sitio la regla de arriba: el código entra por partida doble
 *  —crudo y formateado— y detrás van los textos que cada pantalla añada
 *  (descripción, línea, familia, almacén…). */
export function henoArticulo(
  articulo: string | null | undefined,
  fmt: FmtArticulo,
  ...extras: (string | null | undefined)[]
): string {
  return [codigosBuscables(articulo, fmt), ...extras]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

/** Catálogo de artículos para un `MultiFiltro`.
 *
 *  El VALOR es el código crudo —lo que viaja al predicado y a la URL— y el
 *  TÍTULO el formateado, que es el que enseña la tabla. Antes ambos eran el
 *  crudo, así que un usuario avp leía `QARF32101314` en el desplegable y
 *  `ARF32101314` en la fila: el mismo artículo con dos nombres.
 *
 *  Deduplica por código crudo (gana la primera descripción vista) y ordena por
 *  el título, que es el orden en el que se va a leer. */
export function catalogoArticulos<T>(
  filas: readonly T[],
  codigoDe: (fila: T) => string | null | undefined,
  descripcionDe: (fila: T) => string | null | undefined,
  fmt: FmtArticulo,
): OpcionMulti[] {
  const vistos = new Map<string, string>()
  for (const fila of filas) {
    const codigo = (codigoDe(fila) ?? '').trim()
    if (codigo && !vistos.has(codigo)) vistos.set(codigo, descripcionDe(fila) ?? '')
  }
  return [...vistos.entries()]
    .map(([valor, detalle]) => ({ valor, titulo: fmt(valor), detalle }))
    .sort((a, b) => a.titulo.localeCompare(b.titulo, 'es-ES'))
}

/** Rótulo de un valor según el catálogo: exactamente el mismo que enseña el
 *  desplegable, para que el chip de la franja de filtros activos no diga otra
 *  cosa que la opción de la que salió.
 *
 *  Lo que NO está en el catálogo se devuelve tal cual, y eso es deliberado: en
 *  los campos que además admiten texto libre, un término escrito a mano no es un
 *  código de artículo y pasarlo por el formateador lo mutilaría —el patrón Armaf
 *  casa con cualquier palabra que empiece por letra + «ARF», y «MARFIL» se
 *  quedaría en «ARFIL»—. */
export function etiquetaCatalogoDe(
  opciones: readonly OpcionMulti[],
): (valor: string) => string {
  const mapa = new Map(opciones.map(o => [o.valor, o.titulo]))
  return valor => mapa.get(valor) ?? valor
}
